import type { SceneObject } from '../../core/SceneObject';
import { Properties } from '../properties';
import type { GraphBuilder } from './GraphBuilder';

export type ExpressionType = 'number' | 'boolean' | 'string';
export interface GraphExpressionValue { node: number; socket: 'value'; type: ExpressionType }
type Expr = { type: ExpressionType; literal?: number | boolean | string; property?: string; op?: string; inputs?: Record<string, Expr> };
const precedence: Record<string, number> = { '||': 1, '&&': 2, '==': 3, '!=': 3, '<': 4, '<=': 4, '>': 4, '>=': 4, '+': 5, '-': 5, '*': 6, '/': 6, '**': 7 };
const operations: Record<string, string> = { '+': 'add', '-': 'sub', '*': 'mul', '/': 'div', '**': 'pow', '<': 'lt', '<=': 'le', '>': 'gt', '>=': 'ge', '==': 'eq', '&&': 'and', '||': 'or' };
const literal = (value: number | boolean | string): Expr => ({ type: typeof value as ExpressionType, literal: value });
const op = (name: string, type: ExpressionType, inputs: Record<string, Expr>): Expr => ({ op: `math/${name}`, type, inputs });
function requireType(value: Expr, type: ExpressionType): void {
    if (value.type !== type) throw new Error(`Expected ${type}, received ${value.type}`);
}

/**
 * Parse and type-check before modifying the builder. Expressions are author-time
 * syntax only: the result is ordinary editable native graph data. Reads are live
 * when pulled by an action, and select/boolean operators are not short-circuiting.
 * clamp(min, x, max) follows Canopy's argument order. No eval, calls to scene APIs,
 * member access, assignments or arbitrary JavaScript are accepted.
 */
export function compileGraphExpression(builder: GraphBuilder, source: string, scope: SceneObject, expected?: ExpressionType): GraphExpressionValue {
    if (typeof source !== 'string' || !source.trim() || source.length > 2048) throw new Error('Expression must contain 1–2048 characters');
    const tokens: string[] = [];
    const token = /\s*(\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|\.\d+(?:[eE][+-]?\d+)?|"(?:[^"\\]|\\.)*"|[A-Za-z_][A-Za-z_0-9]*|\*\*|<=|>=|==|!=|&&|\|\||[()+\-*/<>,!?:])/y;
    let offset = 0;
    while (offset < source.trimEnd().length) {
        token.lastIndex = offset;
        const match = token.exec(source);
        if (!match) throw new Error(`Invalid expression syntax at character ${offset + 1}`);
        tokens.push(match[1]); offset = token.lastIndex;
        if (tokens.length > 256) throw new Error('Expression exceeds 256 tokens');
    }
    let cursor = 0, depth = 0;
    const take = () => tokens[cursor++];
    const accept = (value: string) => tokens[cursor] === value ? (cursor++, true) : false;
    const expect = (value: string) => { if (!accept(value)) throw new Error(`Expected "${value}" at token ${cursor + 1}`); };
    function readProperty(name: string): Expr {
        for (let current: SceneObject | null = scope; current; current = current.getParent()) {
            const declaration = current.getBehavior(Properties)?.getProperty(name);
            if (!declaration) continue;
            if (!['number', 'boolean', 'string'].includes(declaration.type)) throw new Error(`Property "${name}" has unsupported expression type ${declaration.type}`);
            return { type: declaration.type as ExpressionType, property: name };
        }
        throw new Error(`No declared property "${name}" in scope "${scope.name}" or its ancestors`);
    }
    function primary(): Expr {
        const value = take();
        if (value === '(') { const result = parse(); expect(')'); return result; }
        if (value === '!' || value === '-' || value === '+') {
            const a = parse(7);
            requireType(a, value === '!' ? 'boolean' : 'number');
            return value === '!' ? op('not', 'boolean', { a }) : value === '-' ? op('sub', 'number', { a: literal(0), b: a }) : a;
        }
        if (value?.startsWith('"')) {
            // The tokenizer accepts any backslash escape; JSON.parse accepts only JSON's.
            try { return literal(JSON.parse(value) as string); }
            catch { throw new Error(`Invalid string literal ${value} at token ${cursor}`); }
        }
        if (value === 'true' || value === 'false') return literal(value === 'true');
        if (value && /^[\d.]/.test(value)) {
            const number = Number(value);
            if (!Number.isFinite(number)) throw new Error('Numeric literals must be finite');
            return literal(number);
        }
        if (value && /^[A-Za-z_]/.test(value)) {
            if (!accept('(')) return readProperty(value);
            const args: Expr[] = [];
            if (!accept(')')) { do { args.push(parse()); } while (accept(',')); expect(')'); }
            const arity: Record<string, number> = { min: 2, max: 2, pow: 2, floor: 1, abs: 1, clamp: 3 };
            if (!Object.hasOwn(arity, value) || args.length !== arity[value]) throw new Error(`Supported functions: min(a,b), max(a,b), pow(a,b), floor(x), abs(x), clamp(min,x,max); invalid call to ${value}`);
            args.forEach(a => requireType(a, 'number'));
            if (value === 'clamp') return op('min', 'number', { a: op('max', 'number', { a: args[0], b: args[1] }), b: args[2] });
            if (value === 'abs') return op('max', 'number', { a: args[0], b: op('sub', 'number', { a: literal(0), b: args[0] }) });
            return op(value, 'number', { a: args[0], ...(args[1] ? { b: args[1] } : {}) });
        }
        throw new Error(`Expected a value at token ${cursor}`);
    }
    function parse(minimum = 0): Expr {
        if (++depth > 32) throw new Error('Expression nesting exceeds 32');
        let a = primary();
        while (Object.hasOwn(precedence, tokens[cursor]) && precedence[tokens[cursor]] >= minimum) {
            const operator = take();
            const b = parse(precedence[operator] + (operator === '**' ? 0 : 1));
            const equality = operator === '==' || operator === '!=';
            const boolean = operator === '&&' || operator === '||';
            if (equality) requireType(b, a.type);
            else { requireType(a, boolean ? 'boolean' : 'number'); requireType(b, a.type); }
            const type = equality || boolean || precedence[operator] === 4 ? 'boolean' : 'number';
            a = operator === '!=' ? op('not', 'boolean', { a: op('eq', 'boolean', { a, b }) }) : op(operations[operator], type, { a, b });
        }
        if (minimum === 0 && accept('?')) {
            requireType(a, 'boolean');
            const yes = parse(); expect(':'); const no = parse(); requireType(no, yes.type);
            a = op('select', yes.type, { condition: a, a: yes, b: no });
        }
        depth--; return a;
    }
    const tree = parse();
    if (cursor !== tokens.length) throw new Error(`Unexpected token "${tokens[cursor]}"`);
    if (expected) requireType(tree, expected);
    const cache = new Map<Expr, number>();
    const properties = new Map<string, number>();
    function emit(expr: Expr): number {
        const existing = cache.get(expr);
        if (existing !== undefined) return existing;
        if (expr.property) {
            let node = properties.get(expr.property);
            if (node === undefined) { node = builder.addGetPropertyNode(scope, expr.property); properties.set(expr.property, node); }
            cache.set(expr, node); return node;
        }
        // A constant at the root still needs a data output for fluent consumers.
        const node = builder._addNode(expr.op ?? 'math/select', {});
        cache.set(expr, node);
        const inputs = expr.inputs ?? { condition: literal(true), a: expr, b: expr };
        for (const [socket, value] of Object.entries(inputs)) {
            if (value.literal !== undefined) builder._setValue(node, socket, value.literal);
            else builder._setValueRef(node, socket, emit(value), 'value');
        }
        return node;
    }
    return { node: emit(tree), socket: 'value', type: tree.type };
}
